import { io } from 'socket.io-client';

/**
 * Network Monitor Service.
 *
 * Supports two transport modes:
 *   1. **Extension bridge** -- the Automatrix Chrome extension injects a
 *      content script (frontend-bridge.js) that relays messages between this
 *      page and the extension's background service worker via
 *      window.postMessage.  The background worker captures requests using the
 *      chrome.webRequest API.
 *
 *   2. **WebSocket (Socket.IO)** -- falls back to a Socket.IO connection to
 *      the backend gateway at /network-monitor.  This mode is used when the
 *      extension is not installed.
 *
 * The class auto-detects which mode to use during connect().
 */

const BRIDGE_SOURCE = 'automatrix-extension';
const FRONTEND_SOURCE = 'automatrix-frontend';

class NetworkMonitorService {
  constructor() {
    this.socket = null;
    this.listeners = new Map();
    this.mode = null; // 'extension' | 'websocket' | null
    this._extensionReady = false;
    this._bridgeHandler = null;
    this._pendingRequests = new Map(); // requestId -> { resolve, reject, timer }
    this._pollInterval = null;
    this._requestCallbacks = [];
    this._lastSeenCount = 0;
  }

  /**
   * Attempt to connect. Tries the extension bridge first, then falls back to
   * WebSocket.
   */
  async connect() {
    // Try extension bridge first
    const extensionAvailable = await this._detectExtension();
    if (extensionAvailable) {
      this.mode = 'extension';
      this._setupBridgeListener();
      return;
    }

    // Fall back to WebSocket
    return this._connectWebSocket();
  }

  /**
   * Detect if the Automatrix extension bridge is available by listening for
   * EXTENSION_READY or sending a PING and waiting for a response.
   */
  _detectExtension() {
    return new Promise((resolve) => {
      // If we already got an EXTENSION_READY event, we're good
      if (this._extensionReady) {
        resolve(true);
        return;
      }

      let resolved = false;

      // Listen for the EXTENSION_READY announcement or a PING response
      const handler = (event) => {
        if (event.data?.source === BRIDGE_SOURCE) {
          if (event.data.type === 'EXTENSION_READY' || event.data.payload?.success) {
            if (!resolved) {
              resolved = true;
              window.removeEventListener('message', handler);
              this._extensionReady = true;
              resolve(true);
            }
          }
        }
      };

      window.addEventListener('message', handler);

      // Also send a PING to trigger a response
      window.postMessage(
        {
          source: FRONTEND_SOURCE,
          requestId: 'ping-' + Date.now(),
          payload: { action: 'PING' },
        },
        '*'
      );

      // Timeout after 1 second -- extension not available
      setTimeout(() => {
        if (!resolved) {
          resolved = true;
          window.removeEventListener('message', handler);
          resolve(false);
        }
      }, 1000);
    });
  }

  /**
   * Set up the persistent message listener for bridge responses and push
   * events.
   */
  _setupBridgeListener() {
    if (this._bridgeHandler) return;

    this._bridgeHandler = (event) => {
      if (event.data?.source !== BRIDGE_SOURCE) return;

      const { requestId, payload } = event.data;

      // Handle EXTENSION_READY (re-announce after navigation)
      if (event.data.type === 'EXTENSION_READY') {
        this._extensionReady = true;
        return;
      }

      // Resolve pending request/response
      if (requestId && this._pendingRequests.has(requestId)) {
        const { resolve, timer } = this._pendingRequests.get(requestId);
        clearTimeout(timer);
        this._pendingRequests.delete(requestId);
        resolve(payload);
      }
    };

    window.addEventListener('message', this._bridgeHandler);
  }

  /**
   * Send a message to the extension via the bridge and wait for a response.
   */
  _sendBridgeMessage(payload, timeoutMs = 5000) {
    return new Promise((resolve, reject) => {
      const requestId = 'req-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8);

      const timer = setTimeout(() => {
        this._pendingRequests.delete(requestId);
        reject(new Error('Bridge message timeout'));
      }, timeoutMs);

      this._pendingRequests.set(requestId, { resolve, reject, timer });

      window.postMessage(
        {
          source: FRONTEND_SOURCE,
          requestId,
          payload,
        },
        '*'
      );
    });
  }

  _connectWebSocket() {
    const wsUrl = window.location.origin;
    this.socket = io(`${wsUrl}/network-monitor`, {
      transports: ['websocket'],
      auth: {
        token: localStorage.getItem('token'),
      },
    });
    return new Promise((resolve, reject) => {
      this.socket.on('connect', () => {
        this.mode = 'websocket';
        resolve();
      });
      this.socket.on('connect_error', (err) => reject(err));
    });
  }

  disconnect() {
    // Stop polling
    this._stopPolling();

    // Clean up bridge listener
    if (this._bridgeHandler) {
      window.removeEventListener('message', this._bridgeHandler);
      this._bridgeHandler = null;
    }

    // Clean up pending bridge requests
    for (const [, { reject, timer }] of this._pendingRequests) {
      clearTimeout(timer);
      reject(new Error('Disconnected'));
    }
    this._pendingRequests.clear();

    // Clean up WebSocket
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }

    this.mode = null;
    this._extensionReady = false;
    this._requestCallbacks = [];
    this._lastSeenCount = 0;
  }

  isConnected() {
    if (this.mode === 'extension') return this._extensionReady;
    return this.socket?.connected || false;
  }

  // ---------------------------------------------------------------------------
  // Capture controls
  // ---------------------------------------------------------------------------

  async startCapture() {
    if (this.mode === 'extension') {
      await this._sendBridgeMessage({ action: 'START_NETWORK_CAPTURE' });
      this._startPolling();
    } else {
      this.socket?.emit('capture:start');
    }
  }

  async stopCapture() {
    if (this.mode === 'extension') {
      this._stopPolling();
      await this._sendBridgeMessage({ action: 'STOP_NETWORK_CAPTURE' });
    } else {
      this.socket?.emit('capture:stop');
    }
  }

  async clearCapture() {
    if (this.mode === 'extension') {
      this._lastSeenCount = 0;
      await this._sendBridgeMessage({ action: 'CLEAR_NETWORK_REQUESTS' });
    }
  }

  // ---------------------------------------------------------------------------
  // Polling (extension bridge mode)
  // ---------------------------------------------------------------------------

  _startPolling() {
    this._stopPolling();
    this._lastSeenCount = 0;
    this._pollInterval = setInterval(() => this._poll(), 500);
  }

  _stopPolling() {
    if (this._pollInterval) {
      clearInterval(this._pollInterval);
      this._pollInterval = null;
    }
  }

  async _poll() {
    try {
      const response = await this._sendBridgeMessage(
        { action: 'GET_NETWORK_REQUESTS' },
        3000
      );

      if (!response || !response.success || !Array.isArray(response.requests)) return;

      const allRequests = response.requests;
      // Only emit new requests since last poll
      const newRequests = allRequests.slice(this._lastSeenCount);
      this._lastSeenCount = allRequests.length;

      for (const raw of newRequests) {
        // Normalize extension webRequest fields to the format the UI expects
        const req = {
          ...raw,
          status: raw.status ?? raw.statusCode ?? null,
          size: raw.size ?? null,
          headers: raw.responseHeaders || raw.headers || null,
          resourceType: raw.resourceType || raw.type || 'other',
        };
        for (const cb of this._requestCallbacks) {
          try {
            cb(req);
          } catch (e) {
            // ignore callback errors
          }
        }
      }
    } catch {
      // Polling error -- extension may have been unloaded; ignore
    }
  }

  // ---------------------------------------------------------------------------
  // Event callbacks
  // ---------------------------------------------------------------------------

  onNewRequest(callback) {
    if (this.mode === 'extension') {
      this._requestCallbacks.push(callback);
    } else {
      this.socket?.on('request:new', callback);
    }
  }

  offNewRequest(callback) {
    if (this.mode === 'extension') {
      this._requestCallbacks = this._requestCallbacks.filter((cb) => cb !== callback);
    } else {
      this.socket?.off('request:new', callback);
    }
  }

  watchTab(tabId) {
    this.socket?.emit('watch:tab', { tabId });
  }

  unwatchTab(tabId) {
    this.socket?.emit('unwatch:tab', { tabId });
  }

  applyFilter(rules, tabId) {
    if (this.mode === 'extension') {
      this._sendBridgeMessage({ action: 'APPLY_NETWORK_FILTERS', rules, tabId }).catch(() => {});
    } else {
      this.socket?.emit('filter:apply', { rules, tabId });
    }
  }

  onFilterApplied(callback) {
    this.socket?.on('filter:applied', callback);
  }

  onElementPicked(callback) {
    this.socket?.on('element:picked', callback);
  }

  offFilterApplied(callback) {
    this.socket?.off('filter:applied', callback);
  }

  offElementPicked(callback) {
    this.socket?.off('element:picked', callback);
  }

  // ---------------------------------------------------------------------------
  // Content Blocker controls (extension bridge only)
  // ---------------------------------------------------------------------------

  async getBlockerStatus() {
    if (this.mode !== 'extension') return { enabled: false, ruleCount: 0 };
    try {
      const res = await this._sendBridgeMessage({ action: 'GET_BLOCKER_STATUS' }, 3000);
      return res && res.success ? res : { enabled: false, ruleCount: 0 };
    } catch {
      return { enabled: false, ruleCount: 0 };
    }
  }

  async toggleContentBlocker() {
    if (this.mode !== 'extension') return { enabled: false };
    try {
      const res = await this._sendBridgeMessage({ action: 'TOGGLE_CONTENT_BLOCKER' }, 5000);
      return res && res.success ? res : { enabled: false };
    } catch {
      return { enabled: false };
    }
  }

  async reloadFilters() {
    if (this.mode !== 'extension') return { ruleCount: 0 };
    try {
      const res = await this._sendBridgeMessage({ action: 'RELOAD_FILTERS' }, 10000);
      return res && res.success ? res : { ruleCount: 0 };
    } catch {
      return { ruleCount: 0 };
    }
  }
}

const networkMonitorService = new NetworkMonitorService();
export default networkMonitorService;
